import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { colorBackgroundPrimary } from '@liveauctioneers/hammer-ui-theme/vars';
import { useCallback, useMemo, useState, type ComponentProps } from 'react';

import type { FilterAction } from '@/types/SearchFacet';

import { useFlatFacetOptions } from '@/pages/Search/SearchPage/hooks/useFlatFacetOptions';
import { MAX_FILTER_OPTIONS_COUNT, type CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getAggregationsOnDemandStatus } from '@/redux/modules/search/search.selectors';
import { ElasticAggregateDemandStatus } from '@/redux/modules/search/search.types';
import { useTrackQuickFilterPillClicked, type QuickFilterName } from '@/utils/amplitude/analytics/searchPage';

import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';

type Props = {
    facetId: string;
    /**
     * Hardcoded flat facets (stylePeriod, creator, materialsAndTechniques) pass
     * their dedicated FilterAction values. Dynamic Attrs.* facets (subject)
     * omit these and fall back to the generic AttrFacet* actions -- same
     * defaults AttributeFacetFilter uses in the AllFilters drawer.
     */
    filterActionChanged?: FilterAction;
    filterActionRemoved?: FilterAction;
    label: string;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
    openMenuId: CurrentOpenMenu;
    currentOpenMenu?: CurrentOpenMenu;
    quickFilterName: QuickFilterName;
};

/**
 * Multi-select facet pill rendered in the SearchFilters row as a chip +
 * dropdown menu (one of N per query). Handles hardcoded flat facets and
 * dynamic Attrs.* facets via the same primitive -- wrappers bind facetId,
 * label, and (for hardcoded facets only) FilterAction analytics.
 */
export const FacetOptionsPill = ({
    currentOpenMenu,
    facetId,
    filterActionChanged,
    filterActionRemoved,
    label,
    onMenuClose,
    onMenuOpen,
    openMenuId,
    quickFilterName,
}: Props) => {
    const { formatMessage } = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();

    const { filteredOptions, isOptionChecked, onOptionClick } = useFlatFacetOptions({
        currentOpenMenu: openMenuId,
        facetId,
        filterActionChanged,
        filterActionRemoved,
    });

    // Pill-only client-side cap: the shared hook returns the full sorted list;
    // the pill renders at most MAX_FILTER_OPTIONS_COUNT when collapsed so the
    // menu height stays bounded inside ConditionalMenuContainer. The drawer
    // skips this cap because its ViewMore is server-paginated.
    const visibleOptions = useMemo(
        () => (isExpanded ? filteredOptions : filteredOptions.slice(0, MAX_FILTER_OPTIONS_COUNT)),
        [isExpanded, filteredOptions]
    );

    const handleMenuOpen: ComponentProps<typeof ConditionalMenuContainer>['onMenuOpen'] = useCallback(
        (menu) => {
            // Track only the chip-open click, not each option toggle.
            trackQuickFilterPillClicked(quickFilterName);
            onMenuOpen(menu);
        },
        [onMenuOpen, quickFilterName, trackQuickFilterPillClicked]
    );

    // Empty options has two shapes:
    //   - Idle / Loading: skipAggs initial load, options not yet hydrated.
    //     Show a placeholder chip whose click triggers fetchSearchAggregationsOnDemand
    //     via the parent's handleMenuOpen. Without the placeholder, the user has
    //     no chip to click and the fetch never fires.
    //   - Loaded / Error: facet genuinely empty (or fetch failed). Hide the pill --
    //     no recovery path.
    if (filteredOptions.length === 0) {
        const aggregationsAreHydrating =
            aggregationsOnDemandStatus === ElasticAggregateDemandStatus.Idle ||
            aggregationsOnDemandStatus === ElasticAggregateDemandStatus.Loading;
        if (aggregationsAreHydrating) {
            return (
                <ConditionalMenuContainer
                    currentOpenMenu={currentOpenMenu}
                    currentOpenMenuId={openMenuId}
                    onMenuClose={onMenuClose}
                    onMenuOpen={handleMenuOpen}
                    title={label}
                    trigger={<Chip style="singleFilter">{label}</Chip>}
                >
                    <Loading />
                </ConditionalMenuContainer>
            );
        }
        return null;
    }

    // Compute against the full sorted list, not the visible slice, so a
    // selected option that falls beyond MAX_FILTER_OPTIONS_COUNT still
    // marks the chip as selected. (The pre-refactor code couldn't do this
    // because `filteredOptions` was the already-sliced list.)
    const hasSelected = filteredOptions.some(isOptionChecked);

    return (
        <ConditionalMenuContainer
            currentOpenMenu={currentOpenMenu}
            currentOpenMenuId={openMenuId}
            onMenuClose={onMenuClose}
            onMenuOpen={handleMenuOpen}
            title={label}
            trigger={
                <Chip
                    selected={hasSelected}
                    style="singleFilter"
                >
                    {label}
                </Chip>
            }
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor={`rgb(var(${colorBackgroundPrimary}))`}
                onChange={(expanded) => {
                    setIsExpanded(expanded);
                }}
                textCutoffHeight={200}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                >
                    {visibleOptions.map((option) => (
                        <Checkbox.Option
                            checked={isOptionChecked(option)}
                            disabled={option.count === 0 && !isOptionChecked(option)}
                            key={option.id}
                            onChange={() => {
                                onOptionClick(option);
                            }}
                        >
                            {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                        </Checkbox.Option>
                    ))}
                </div>
            </ViewMore>
        </ConditionalMenuContainer>
    );
};
